import { Component, ElementRef, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import RecordRequest from 'src/app/interfaces/RequestRecords/RecordRequest';
import VolumePricingTier from 'src/app/interfaces/RequestRecords/VolumePricingTier';
import { UserProfile } from 'src/app/interfaces/UserProfile';
import AuditDocument from 'src/app/interfaces/auditDocument';
import { AuditService } from 'src/app/services/audit.service';
import { GeneralService } from 'src/app/services/general.service';
import NationWithStates from 'src/app/interfaces/NationWithStates';
import { DateTime } from 'luxon';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { RecordRequestService } from 'src/app/services/record-request.service';
import DriverInfoRequest from 'src/app/interfaces/RequestRecords/driverInfoRequest';
import { Router } from '@angular/router';
import { ConfigDataService } from 'src/app/services/config-data.service';
import NationState from 'src/app/interfaces/NationState';
import { TranslatorService } from 'src/app/services/translator.service';
import License from 'src/app/interfaces/RequestRecords/license';
import { UserService } from 'src/app/services/user.service';
import { Auth } from 'src/app/interfaces/auth';
import TextValidation from 'src/app/interfaces/RequestRecords/textValidation';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { audit } from 'rxjs';

@Component({
    selector: 'app-requestrecords',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './requestrecords.component.html',
    styleUrls: ['./requestrecords.component.scss'],
    standalone: false
})
export class RequestrecordsComponent {
    userProfile?: UserProfile;
    isSaving: boolean = false;
    validating: boolean = false;
    errorSaving: boolean = false;
    isValid: boolean = false;
    minutesWaitPer100: number = 1;
    pricingTiers: VolumePricingTier[] = [];
    currentPricingTier: VolumePricingTier = { waitTimeMessage: '', costPer: 0, maxCount: 0, minCount: 0 };
    recordRequests: RecordRequest[] = [];
    lastNameRegex: RegExp = /^([a-zA-Z]*)$/;
    firstNameRegex: RegExp = /^([a-zA-Z ']*)$/;
    licenseNumberRegex: RegExp = /^([a-zA-Z0-9]*)$/;
    groupedNationStates: NationWithStates[] = [];
    serverValidationErrors: string[] = [];
    onProbation: boolean = false;
    showUploadModal = false;
    isUploading = false;
    isLoading = false;
    probationDocuments: AuditDocument[] = [];
    spanish: boolean = false;
    french: boolean = false;
    isSuperUser: boolean = false;
    isCustomerUserWithPermission: boolean = false;
    isDriver: boolean = false;
    isValidFileType: boolean = true;
    hasServerException: boolean = false;
    allNationStates: [] = [];
    nationStatesLang: string = '';
    dobRegex: RegExp = /\d{8}/g;
    minDateOfBirth: DateTime | any;
    invFileTypeModal: any;

    @ViewChild('UploadModal') uploadModal: TemplateRef<any> | undefined;
    @ViewChild('InvalidFileTypeModal') invalidFileTypeModal: TemplateRef<any> | undefined;
    @ViewChild('uploadDocumentsId') uploadDocumentsInput: ElementRef | undefined;

    get numberOfRows(): number {
        return this.recordRequests.length + 1;
    }

    get count(): number {
        return this.recordRequests.length;
    }

    get pricePer(): number {
        return this.currentPricingTier.costPer;
    }

    get transactionTotal(): number {
        return this.count * this.currentPricingTier.costPer;
    }

    constructor(private generalService: GeneralService,
      private authenticationService: AuthenticationService,
      private auditService: AuditService,
      private recordRequestService: RecordRequestService,
      private router: Router,
      private configDataService: ConfigDataService,
      private translationService: TranslatorService,
      private userService: UserService,
      private modalService: NgbModal) { 
        this.validateTermsAndConditions();
        this.addNewRecordRequest();
       }

     ngOnInit(): void {
          this.auditService.auditFiles = [];
          window.onbeforeunload = this.unloadMessage;
          this.userProfile = this.authenticationService.getUserProfile() as UserProfile;


          // obtain the probation status of this customer.
          if (this.userProfile && this.userProfile.customer && this.userProfile.customer.auditPenalty &&
               this.userProfile!.customer!.auditPenalty!.penaltyStartDate &&
               this.userProfile!.customer!.auditPenalty!.penaltyEndDate) {
               const nowMoment = DateTime.now();
               const start = DateTime.fromISO(this.userProfile!.customer!.auditPenalty!.penaltyStartDate.toString());
               const end = DateTime.fromISO(this.userProfile!.customer!.auditPenalty!.penaltyEndDate.toString());
               if (this.userProfile!.customer!.auditPenalty!.auditPenaltyActionId === 1 &&
                    start <= nowMoment && end >= nowMoment) {
                    this.onProbation = true;
               }
          }

          this.minDateOfBirth = DateTime.now().minus({ year: 18 });

          const statesPromise = this.generalService.getStates();
          statesPromise.then((v: any) => this.loadNationStates(v));

          this.getPricingTierConfigInfo();

          setTimeout(() => this.scrollToTop(), 500);
     }

    submitRequests() {
        this.isSaving = true;
        window.scrollTo(0, 0);
        this.refreshSession();
        const driverInfoRequests = this.recordRequestService.map(this.recordRequests, this.probationDocuments);
        const validationPromise = this.recordRequestService.serverValidateRequests(driverInfoRequests);
        validationPromise.then((v: any) => this.handleValidatedRequest(v, driverInfoRequests));
    }

    handleValidatedRequest(validationResponse: [] | any, driverInfoRequests: DriverInfoRequest[]) {
        this.hasServerException = false;
        const sessionExpired = validationResponse && validationResponse.session && validationResponse.session === false;
        const hasValidationErrors = validationResponse && validationResponse.length > 0;
        
        if (sessionExpired) {
            this.authenticationService.logout();
            this.router.navigateByUrl('/signin');
        } else if (hasValidationErrors) {
            if (Array.isArray(validationResponse)) {
                this.serverValidationErrors = validationResponse;
            } else {
                this.hasServerException = true;
                const message = this.translate(validationResponse, []);
                this.serverValidationErrors.push(message);
            }
            this.isSaving = false;
        } else {
            // Success!
            const submitPromise = this.recordRequestService.submitRequests(driverInfoRequests, 'CustomerUI', 'Login.gov');
            submitPromise.then(() => {
                window.onbeforeunload = null;
                if (this.onProbation) {
                    this.router.navigateByUrl('/probationOrderSuccess')
                } else {
                    this.router.navigateByUrl('/dashboard');
                }
            });
        }
    }

    getPricingTierConfigInfo() {
        let countThresholds: number[] = [];
        let pricePerUnits: number[] = [];
        let waitTimes: string[] = [];

        const countThresholdPromise = this.configDataService.getConfig('VolumePricingRecordCountThresholds');
        countThresholdPromise.then((thresholdResult: any) => {
            const countThresholdString = thresholdResult;
            const pricePerUnitPromise = this.configDataService.getConfig('VolumePricingPricePerUnit');
            pricePerUnitPromise.then((pricePerResult: any) => {
                const pricePerUnitString = pricePerResult;
                const waitTimePromise = this.configDataService.getConfig('VolumePricingWaitTime');
                waitTimePromise.then((waitTimeResult: any) => {
                    const waitTimeString = waitTimeResult;

                    if (countThresholdString && countThresholdString.length > 0 &&
                        pricePerUnitString && pricePerUnitString.length > 0 &&
                        waitTimeString && waitTimeString.length > 0) {
                        
                        countThresholds = countThresholdString.split(',');
                        pricePerUnits = pricePerUnitString.split(',');
                        waitTimes = waitTimeString.split(',');
                    }
                    if (countThresholds.length === 0 || countThresholds.length !== pricePerUnits.length || countThresholds.length !== waitTimes.length) {
                        console.error('Config values for Volume Pricing have different lengths. Fix this ASAP! Values are in the database.')
                    }
            
                    let minCount = 0;
                    for (let index = 0; index < countThresholds.length; index++) {
                        const maxCount = countThresholds[index];
                        const pricePerUnit = pricePerUnits[index];
                        const waitMessage = waitTimes[index];
                        
                        this.pricingTiers.push({
                            minCount: minCount,
                            maxCount: maxCount,
                            costPer: pricePerUnit,
                            waitTimeMessage: waitMessage
                        });
                        minCount = maxCount;
                    }
                    this.currentPricingTier = this.pricingTiers[0];
                }).catch((error: any) => {
                    console.error(error);
                });
            }).catch((error: any) => {
                console.error(error);
            });
        }).catch((error: any) => {
            console.error(error);
        });
        
        const minutesWaitPer100Promise = this.configDataService.getConfig('VolumePricingMinWaitPer100');
        minutesWaitPer100Promise.then((waitPer100Result: any) => {
            if (waitPer100Result && waitPer100Result.length > 0) {
                this.minutesWaitPer100 = waitPer100Result;
            }
        }).catch((error: any) => {
            console.error(error);
        });
    }

    loadNationStates(states: []) {
        this.groupedNationStates = [];
        if (this.allNationStates.length === 0) {
            this.allNationStates = states;
            this.nationStatesLang = this.translationService.getActiveLanguage().toUpperCase();
        }
        
        const map = new Map();
        states.forEach((nationState: NationState) => {
            const key:string = nationState.country;
            const collection = map.get(key);
            if (!collection) {
                map.set(key, [nationState.state]);
            } else {
                if(nationState.state === 'PQ'){
                    nationState.state = 'PQ/QC'
                }
                collection.push(nationState.state);

            }
        });
        
        map.forEach((array, key) => {
            const translatedNation = this.generalService.getTranslation(key, 'base');
            const group: NationWithStates = {
                country: translatedNation,
                states: array
            };
            this.groupedNationStates.push(group);
        });
    }

    addNewRecordRequest() {
        this.recordRequests.push({
            index: this.count,
            lastName: '',
            firstName: '',
            dateOfBirth: undefined,
            dateOfBirthEntry: '',
            internalReference: '',
            licenses: [
                {
                    index: 0,
                    lastName: '',
                    licenseNumber: '',
                    licenseState: '',
                    lastNameValidation: this.createNewTextValidation(true, this.lastNameRegex, 30),
                    licenseNumberValidation: this.createNewTextValidation(true, this.licenseNumberRegex, 20),
                    licenseStateValidation: this.createNewTextValidation(true, null, null)
                }
            ],
            disclosureAndAuthorizationCertified: false,
            dobValidation: {
                isTooYoungError: false,
                hasInvalidDOBError: false,
                hasStandardDOBError: false,
                isDirty: false,
                isValid: false,
            },
            lastNameValidation: this.createNewTextValidation(true, this.lastNameRegex, 30),
            firstNameValidation: this.createNewTextValidation(true, this.firstNameRegex, 30),
            internalReferenceValidation: this.createNewTextValidation(false, null, 256),
            hasDuplicateLicense: false
        });
        this.refreshSession();
        this.updatePricingTier();
    
        // wait 1/2 second then set focus to the new record request.
        setTimeout(() => {                                  
            document.getElementById('driverLastName_input_' + (this.count-1))?.focus();                       
        }, 500);
    
    }

    removeRecordRequest(r: RecordRequest) {
        this.recordRequests = this.recordRequests.filter((rr: RecordRequest) => rr.index !== r.index);
        for (let index = 0; index < this.recordRequests.length; index++) {
            this.recordRequests[index].index = index;
        }
        this.updatePricingTier();
    }

    addLicense(r: RecordRequest) {
        r.licenses.push({
            index: r.licenses.length,
            lastName: '',
            licenseNumber: '',
            licenseState: '',
            lastNameValidation: this.createNewTextValidation(true, this.lastNameRegex, 30),
            licenseNumberValidation: this.createNewTextValidation(true, this.licenseNumberRegex, 20),
            licenseStateValidation: this.createNewTextValidation(true, null, null)
        });
        this.checkForDuplicateLicenses(r);
    }

    removeLicense(r:RecordRequest, dl:License) {
        r.licenses = r.licenses.filter((l: License) => l.index !== dl.index);
        for (let index = 0; index < r.licenses.length; index++) {
            r.licenses[index].index = index;
        }
        this.checkForDuplicateLicenses(r);
    }

    checkForDuplicateLicenses(r: RecordRequest) {
        const copyOfLicenses = r.licenses.map((l: License) => l);
        let hasDuplicateLicense = false;

        for (let i = 0; i < copyOfLicenses.length; i++) {
            for (let j = 0; j < r.licenses.length; j++) {
                if (i !== j) {
                    if (i !== j &&
                        copyOfLicenses[i].lastName.toUpperCase() === r.licenses[j].lastName.toUpperCase() &&
                        copyOfLicenses[i].licenseNumber.toUpperCase() === r.licenses[j].licenseNumber.toUpperCase() &&
                        copyOfLicenses[i].licenseState.toUpperCase() === r.licenses[j].licenseState.toUpperCase()
                    ) {
                        hasDuplicateLicense = true;
                        break;
                    }
                }
                if (hasDuplicateLicense) {
                    break;
                }
            }
        }
        r.hasDuplicateLicense = hasDuplicateLicense;
    }

    driverInfoLastNameChange(recordRequest: RecordRequest) {
        this.validateText(recordRequest.lastName, recordRequest.lastNameValidation);
        recordRequest.licenses[0].lastName = recordRequest.lastName;
        this.validateText(recordRequest.licenses[0].lastName, recordRequest.licenses[0].lastNameValidation);
    }

    // ------------------ Utility Functions ------------------ //

    formatCurrency(amount: number) {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });
        return formatter.format(amount);
    }

    setFocus(el:any) {
        try{
        if (el.$el.getElementsByTagName('input').length === 4) {
            el.$el.getElementsByTagName('input')[3].focus();
        }}catch(e){
            console.error(e)
        }
    }

    translate(translationTag: string, args: any): string {
        let translatedText: string = this.generalService.getTranslation(translationTag, 'request');
        
        if (translatedText === undefined) {
            translatedText = this.generalService.getTranslation(translationTag, 'request');
        }
        if (args && args.index !== null) {
            translatedText = translatedText.replace(`__xxx__`, (args.index+1) + ''); 
        }

        if (translationTag === 'Voodoo') {
            const lang = this.translationService.getActiveLanguage().toUpperCase();
            const viewScope = this;
            if (lang !== this.nationStatesLang) {
                setTimeout(function () {
                    viewScope.loadNationStates(viewScope.allNationStates);
                    viewScope.nationStatesLang = lang;
                }, 1000);
            }
        }
        return translatedText;
    }



    scrollToTop() {
        window.scrollTo(0, 0);
    }

    getUniqueIdentifier(propertyName: string, htmlElementType: string, additionalIdentifiers: any[] = []) {
        let id = `${propertyName}_${htmlElementType}`;
        for (let index = 0; index < additionalIdentifiers.length; index++) {
            const identifier = additionalIdentifiers[index];
            id += '_' + identifier;
        }
        return id;
    }

    refreshSession() {
        const userString: string = localStorage.getItem('user') as string;
        const user: Auth = JSON.parse(userString);
        this.userService.refresh(user);
    }

    getLicenseTipText() {
        return this.translate('LicenseNumTip', undefined);
    }

    getInternalRefTipText() {
        return this.translate('InternalReferenceHelp', undefined);
    }

    // ------------------ Probation Document Functions ------------------ //
    openUploadModal() {
        this.showUploadModal = true;
        this.modalService.open(this.uploadModal as TemplateRef<any>, { size: 'lg', backdrop: 'static', keyboard: false, ariaLabelledBy: 'uploadModalTitle' });
    }

    closeUploadModal() {
        this.showUploadModal = false;
        this.modalService.dismissAll();
    }
    
    documentUploadComplete(auditDocuments: AuditDocument[] | any) {

        this.probationDocuments = auditDocuments;
        this.isLoading = false;
        this.isUploading = false;
    }

    cancelUploads() {
        this.isLoading = true;
        
        this.probationDocuments.forEach((doc: any) => {
            this.auditService.deleteProbationDocument(doc.docId);
        });
        this.probationDocuments = [];
        this.auditService.auditFiles = [];

        this.isLoading = false;
        this.closeUploadModal();
    }
    
    removeDocument(docId: string, index: number) { 
        const theDoc = this.probationDocuments.filter((d: any) => d.documentId === docId);
        const theDocName = theDoc[0].fileName;

        this.auditService.deleteProbationDocument(docId);
        this.probationDocuments = this.probationDocuments.filter((d: any) => d.documentId !== docId);
        this.auditService.auditFiles = this.auditService.auditFiles.filter((d: any) => d.name !== theDocName);
        this.isLoading = false;
        this.isUploading = false;
    }

    completeDocumentUploads() {
        this.closeUploadModal();
    }

    uploadDocuments(event: any) {
        const username = this.authenticationService.getUserName();
        let files: [];
        if (event.target && event.target.files && event.target.files.length > 0) {
            files = event.target.files;
            for (let index = 0; index < files.length; index++) {
                const file: any = files[index];
                if (file.name) {
                    const nameParts: string[] = file.name.split('.');
                    const fileExtension = nameParts[nameParts.length - 1].toLowerCase();
                    if (fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'png' || fileExtension === 'jpeg') {
                        this.isValidFileType = true;
                        this.auditService.auditFiles.push(file);
                    } else {
                        this.isValidFileType = false;
                        this.invFileTypeModal = this.modalService.open(this.invalidFileTypeModal as TemplateRef<any>, { size: 'lg', backdrop: 'static', keyboard: false, ariaLabelledBy: 'invalidFileTypeModalTitle' });
                         event.target.value = '';
                         return;
                    }
                }
            }
             event.target.value = '';
        } else if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            files = event.dataTransfer.files;
            for (let index = 0; index < files.length; index++) {
                const file: any = files[index];
                if (file.name) {
                    const nameParts: string[] = file.name.split('.');
                    const fileExtension = nameParts[nameParts.length - 1].toLowerCase();
                    if (fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'png' || fileExtension === 'jpeg') {
                        this.isValidFileType = true;
                        this.auditService.auditFiles.push(file);
                    } else {
                        this.isValidFileType = false;
                         this.invFileTypeModal = this.modalService.open(this.invalidFileTypeModal as TemplateRef<any>, { size: 'lg', backdrop: 'static', keyboard: false, ariaLabelledBy: 'invalidFileTypeModalTitle' });
                         event.target.value = '';
                        return;
                    }
                }   
            }
        }

        if (this.isValidFileType) {
            this.isUploading = true;
        
            this.auditService.uploadAuditDocuments('', username, true)
                .then((result: any) => {
                    if (!result) {
                         console.error('Audit document upload error.');
                         event.target.value = '';
                        this.router.navigateByUrl('/applicationException');
                    } else if (result && result === 'unauthorized') {
                         console.warn('User not authorized to upload audit documents.');
                         event.target.value = '';
                        this.router.navigateByUrl('/accessDenied');
                    } else if (result) {
                        this.documentUploadComplete(result);
                    }
                     event.target.value = '';
                }).catch((error: any) => {
                    console.error('Error uploading audit documents');
                     console.error(error);
                     event.target.value = '';
                });
        } else {
             event.target.value = '';
        }
    }

    closeInvalidFileTypeModal() {
        this.isUploading = false;
        this.invFileTypeModal.close();
    }

    showFaxCoverSheet() {
        const thisWindow = window;
        this.isLoading = true;
        const auditServicePromise = this.auditService.getProbationFaxCoversheet(this.recordRequests[0].firstName, this.recordRequests[0].lastName);
        auditServicePromise.then((result: any | any) => {
            this.isLoading = false;
            const faxWindow = thisWindow.open('', '', 'width=800,height=600,resizable=1');
            faxWindow?.document.write(result.data);
            faxWindow?.focus();
            thisWindow.onbeforeunload = null;
        })
        .catch((error: any) => {
            console.error(error);
            thisWindow.onbeforeunload = null;
        });
    }

    unloadMessage = function () {
        return 'You have created an account but not set up billing. Are you sure you wish to exit?';
    }

    // ------------------ Validation Functions ------------------ //

    licenseValueChanged(value: string, validator: TextValidation, r: RecordRequest) {
        this.validateText(value, validator);
        this.checkForDuplicateLicenses(r);
    }

    validateText(value:string, validator: TextValidation) {
        validator.hasRequiredError = false;
        validator.hasInvalidCharacters = false;
        validator.hasTooManyCharacters = false;
        validator.isDirty = true;

        if (validator.isRequired && (!value || value.length === 0)) {
            validator.hasRequiredError = true;
        }

        if (validator.regex && value && !value.match(validator.regex)) {
            validator.hasInvalidCharacters = true;
        }

        if (validator.characterLimit && value && value.length > validator.characterLimit) {
            validator.hasTooManyCharacters = true;
        }

        validator.isValid = validator.hasInvalidCharacters === false &&
            validator.hasRequiredError === false &&
            validator.hasTooManyCharacters === false;
    }

    createNewTextValidation(isRequired: boolean, regex: RegExp | any, characterLimit: number|any): TextValidation {
        return {
            isDirty: false,
            isValid: !isRequired,
            isRequired: isRequired,
            regex: regex,
            characterLimit: characterLimit,
            hasInvalidCharacters: false,
            hasTooManyCharacters: false,
            hasRequiredError: false
        }
    }

    validateDateOfBirth(r: RecordRequest) {
        r.dobValidation.isTooYoungError = false;
        r.dobValidation.hasInvalidDOBError = false;
        r.dobValidation.hasStandardDOBError = false;
        r.dobValidation.isDirty = true;
        r.dateOfBirth = '';

        if (r.dateOfBirthEntry && r.dateOfBirthEntry.match(this.dobRegex)) {
            const momentDOB = DateTime.fromFormat(r.dateOfBirthEntry, 'MMddyyyy');
            const isValid = momentDOB.isValid && momentDOB.year > 1900;
            if (isValid) {
                const isTooYoung = momentDOB >= this.minDateOfBirth;
                r.dobValidation.isTooYoungError = isTooYoung;
                if (!isTooYoung) {
                    r.dateOfBirth = momentDOB.toFormat('MM/dd/yyyy');
                }
            } else {
                r.dateOfBirth = undefined;
                r.dobValidation.hasInvalidDOBError = true;
            }
        } else {
            r.dateOfBirth = undefined;
            r.dobValidation.hasStandardDOBError = true;
        } 
        
        r.dobValidation.isValid = r.dobValidation.hasInvalidDOBError === false &&
            r.dobValidation.hasStandardDOBError === false &&
            r.dobValidation.isTooYoungError === false;

    }

    validateTermsAndConditions() {
        const skipTnC = this.authenticationService.getRoles() !== null && this.authenticationService.getRoles().includes('csr_');
        const hasAgreedToTerms = this.authenticationService.getHasAgreedToTerms();
        if (skipTnC === false && hasAgreedToTerms === false) {
            this.authenticationService.logout();
            this.router.navigateByUrl('/signin');
        }
    }

    isPageValid(errors: any): boolean {
        if (errors && errors.items && errors.items.length > 0) {
            return false;
        }

        // Loop through each request and license. If any are invalid, return false.
        for (let rrIndex = 0; rrIndex < this.recordRequests.length; rrIndex++) {
            const recordRequest = this.recordRequests[rrIndex];
            if (recordRequest.disclosureAndAuthorizationCertified === false ||
                recordRequest.hasDuplicateLicense ||
                recordRequest.firstNameValidation.isValid === false ||
                recordRequest.lastNameValidation.isValid === false ||
                recordRequest.dobValidation.isValid === false ||
                recordRequest.internalReferenceValidation.isValid === false) {
                return false;
            } else {
                for (let dlIndex = 0; dlIndex < recordRequest.licenses.length; dlIndex++) {
                    const license = recordRequest.licenses[dlIndex];
                    if (license.lastNameValidation.isValid === false ||
                        license.licenseNumberValidation.isValid === false ||
                        license.licenseStateValidation.isValid === false) {
                        return false;
                    }
                }
            }   
        }
        return true;
    }

    isFormValidBeforeAthorizationChecked(errors: any): boolean {
        if (errors && errors.items && errors.items.length > 0) {
            return false;
        }

        // Loop through each request and license. If any are invalid, return false.
        for (let rrIndex = 0; rrIndex < this.recordRequests.length; rrIndex++) {
            const recordRequest = this.recordRequests[rrIndex];
            if (recordRequest.hasDuplicateLicense ||
                recordRequest.firstNameValidation.isValid === false ||
                recordRequest.lastNameValidation.isValid === false ||
                recordRequest.dobValidation.isValid === false ||
                recordRequest.internalReferenceValidation.isValid === false) {
                return false;
            } else {
                for (let dlIndex = 0; dlIndex < recordRequest.licenses.length; dlIndex++) {
                    const license = recordRequest.licenses[dlIndex];
                    if (license.lastNameValidation.isValid === false ||
                        license.licenseNumberValidation.isValid === false ||
                        license.licenseStateValidation.isValid === false) {
                        return false;
                    }
                }
            }   
        }
        return true;
    }

    updatePricingTier() {
        const recordCount = this.recordRequests.length;
        const currentTier = this.pricingTiers.find(tier => tier.minCount < recordCount && recordCount <= tier.maxCount);
        if(currentTier){
            this.currentPricingTier = currentTier;
        }
    }
    
}
